import { Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

export interface ClinicalSummaryPanelProps {
  weight_kg: number | null;
  temp_c: number | null;
  heart_rate_bpm: number | null;
  respiratory_rate_bpm: number | null;
  body_condition_score: number | null;
  diagnoses_count: number;
  has_plan: boolean;
  prescriptions_active_count: number;
  studies_count: number;
  attachments_count: number;
  className?: string;
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="bg-muted rounded px-2 py-0.5 font-mono text-xs font-semibold">{value}</span>
    </div>
  );
}

/** "Resumen clínico" — compact read-only rollup of the current visit's vitals + section
 *  counts. Recomputed from data already loaded by ClinicalVisitPanel + the sibling
 *  section queries; no new backend calls beyond count-only reads of existing queries. */
export function ClinicalSummaryPanel({
  weight_kg,
  temp_c,
  heart_rate_bpm,
  respiratory_rate_bpm,
  body_condition_score,
  diagnoses_count,
  has_plan,
  prescriptions_active_count,
  studies_count,
  attachments_count,
  className,
}: ClinicalSummaryPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  return (
    <Card className={cn('border-border/70', className)}>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 p-4 pb-3">
        <div className="flex items-center gap-2">
          <span className="bg-primary/10 text-primary flex h-7 w-7 items-center justify-center rounded-full">
            <Activity className="h-4 w-4" aria-hidden />
          </span>
          <CardTitle className="text-sm font-semibold">{t('clinical.summary_title')}</CardTitle>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? t('clinical.summary_collapse') : t('clinical.summary_expand')}
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden />
          )}
        </Button>
      </CardHeader>
      {expanded ? (
        <CardContent className="space-y-3 p-4 pt-0">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <Row
              label={t('clinical.summary_weight')}
              value={weight_kg != null ? `${weight_kg.toFixed(2)} kg` : `— kg`}
            />
            <Row
              label={t('clinical.summary_temp')}
              value={temp_c != null ? `${temp_c.toFixed(1)} °C` : `— °C`}
            />
            <Row
              label={t('clinical.summary_heart_rate')}
              value={heart_rate_bpm != null ? `${heart_rate_bpm} lpm` : `— lpm`}
            />
            <Row
              label={t('clinical.summary_resp_rate')}
              value={respiratory_rate_bpm != null ? `${respiratory_rate_bpm} rpm` : `— rpm`}
            />
            <Row
              label={t('clinical.summary_bcs')}
              value={body_condition_score != null ? String(body_condition_score) : '—'}
            />
          </div>
          <div className="space-y-2 border-t pt-2">
            <Row label={t('clinical.summary_diagnoses')} value={String(diagnoses_count)} />
            <Row
              label={t('clinical.summary_plan')}
              value={has_plan ? t('clinical.summary_plan_defined') : t('clinical.summary_plan_empty')}
            />
            <Row
              label={t('clinical.summary_prescriptions_active')}
              value={String(prescriptions_active_count)}
            />
            <Row label={t('clinical.summary_studies')} value={String(studies_count)} />
            <Row label={t('clinical.summary_attachments')} value={String(attachments_count)} />
          </div>
          <p className="text-muted-foreground text-[11px] italic">{t('clinical.summary_hint')}</p>
        </CardContent>
      ) : null}
    </Card>
  );
}
